import { ResumeReview, ResumeUploadResponse } from '../types';

// Drawer → editor handoff: navigating to the full-page tailoring editor
// unmounts the search drawer, so its in-progress state is persisted here and
// restored when the user returns. sessionStorage on purpose — the draft is
// tab-scoped and should not outlive the session.
//
// Bump the key suffix only on a breaking change to the SearchDraft shape that
// SHIPPED, so drafts persisted by an older release are ignored instead of
// restored into a drawer that no longer understands them. (loadSearchDraft
// also shape-guards, but the version bump keeps intent explicit.)
//
// Still v1 after remote search: every field it added is optional and absent on
// a released draft, which restores as a local search — so a user who entered
// the tailoring editor before the deploy and returns after it keeps their
// role, review and approved version instead of starting over. The one shape
// that would have needed a bump (hasWorkPermit written unconditionally from a
// defaulted false) existed only in unreleased commits of this branch; the
// restore reads hasWorkPermit as an answer only when it is a boolean.
export const SEARCH_DRAFT_KEY = 'beast.searchDraft.v1';

export interface SearchDraft {
  resumeData: ResumeUploadResponse;
  role: string;
  location: string;
  draftLanguage: string;
  review: ResumeReview | null;
  resumeVersionId?: string; // set after approve
  // At runtime a stored draft can still carry 'tailor-confirm' (written by
  // the removed start-from-tailored flow) — the drawer's restore treats any
  // unknown value as "start at confirm".
  returnStep: 'review' | 'plan';
  // Additive-optional fields (old drafts stay restorable — the shape guard
  // below only requires resumeData.resumeInternalId + returnStep):
  fileName?: string; // uploaded file's display name for the confirm step
  // Remote search mode; absent on drafts saved before the feature → local.
  isRemote?: boolean;
  residence?: string;
  // Present only once the user has answered the permit question (v2). Absent
  // means unanswered, which is not the same as No.
  hasWorkPermit?: boolean;
}

export const saveSearchDraft = (draft: SearchDraft): void => {
  sessionStorage.setItem(SEARCH_DRAFT_KEY, JSON.stringify(draft));
};

export const loadSearchDraft = (): SearchDraft | null => {
  const raw = sessionStorage.getItem(SEARCH_DRAFT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    // Minimum-shape guard: a draft is only restorable if it carries the
    // uploaded resume's id and knows which step to return to. Anything else
    // (draft written by an older/newer code version, manual tampering) is
    // dropped so the drawer starts clean.
    if (!parsed?.resumeData?.resumeInternalId || !parsed?.returnStep) {
      sessionStorage.removeItem(SEARCH_DRAFT_KEY);
      return null;
    }
    return parsed;
  } catch {
    // Corrupt payload (partial write, manual tampering): drop it so the
    // drawer starts clean instead of failing on every open.
    sessionStorage.removeItem(SEARCH_DRAFT_KEY);
    return null;
  }
};

export const clearSearchDraft = (): void => {
  sessionStorage.removeItem(SEARCH_DRAFT_KEY);
};
